import { useState, useContext, useMemo } from "react";
import { Box, Button } from "@mui/material";
import { Layer } from "../../types/layer";
import { DrawingContext, DrawingMode, DrawingFeatureType } from "../../contexts/DrawingContext";
import LayerControlItem from "./LayerControlItem";
import FilterModal, { Filter } from "./FilterModal";
import { getTreatmentFilterAttributes } from "../../utils/treatmentFilters";
import { useAuth } from "../../contexts/AuthContext";
import { isViewOnly } from "../../utils/permissions";

interface TreatmentsControlProps {
  layer: Layer | null;
  filters: Filter[];
  onFiltersChange: (filters: Filter[]) => void;
  expanded?: boolean;
  onExpandedChange?: (expanded: boolean) => void;
}

const TreatmentsControl: React.FC<TreatmentsControlProps> = ({ layer, filters, onFiltersChange, expanded, onExpandedChange }) => {
  const { drawingMode, setDrawingMode, setDrawingFeatureType } = useContext(DrawingContext);
  const { user } = useAuth();
  const viewOnly = isViewOnly(user);
  const [filterModalOpen, setFilterModalOpen] = useState(false);

  const attributes = useMemo(() => {
    return layer?.rawSource ? getTreatmentFilterAttributes(layer.rawSource) : [];
  }, [layer?.rawSource]);

  if (!layer) return null;

  const handleToggleVisibility = () => {
    layer.toolbarButton?.props?.onClick?.();
  };

  const handleDrawClick = (featureType: string) => {
    if (drawingMode === DrawingMode.TREATMENT) {
      setDrawingMode(null);
      setDrawingFeatureType(null);
    } else {
      setDrawingMode(DrawingMode.TREATMENT);
      setDrawingFeatureType(featureType);
    }
  };

  const handleFiltersChange = (newFilters: Filter[]) => {
    onFiltersChange(newFilters);
  };

  const drawingContent = viewOnly ? null : (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
      <Button
        size="small"
        variant={drawingMode === DrawingMode.TREATMENT ? "contained" : "outlined"}
        onClick={() => handleDrawClick(DrawingFeatureType.POLYGON)}
        sx={{
          color: drawingMode === DrawingMode.TREATMENT ? "#fff" : "#00d4ad",
          borderColor: "#00d4ad",
          backgroundColor: drawingMode === DrawingMode.TREATMENT ? "#00d4ad" : "transparent",
          "&:hover": {
            backgroundColor: drawingMode === DrawingMode.TREATMENT ? "#00b899" : "rgba(0, 212, 173, 0.1)",
          },
        }}
      >
        Draw Polygon
      </Button>
      <Button
        size="small"
        variant={drawingMode === DrawingMode.TREATMENT ? "contained" : "outlined"}
        onClick={() => handleDrawClick(DrawingFeatureType.LINE)}
        sx={{
          color: drawingMode === DrawingMode.TREATMENT ? "#fff" : "#00d4ad",
          borderColor: "#00d4ad",
          backgroundColor: drawingMode === DrawingMode.TREATMENT ? "#00d4ad" : "transparent",
          "&:hover": {
            backgroundColor: drawingMode === DrawingMode.TREATMENT ? "#00b899" : "rgba(0, 212, 173, 0.1)",
          },
        }}
      >
        Draw Line
      </Button>
      <Button
        size="small"
        variant={drawingMode === DrawingMode.TREATMENT ? "contained" : "outlined"}
        onClick={() => handleDrawClick(DrawingFeatureType.UPLOAD)}
        sx={{
          color: drawingMode === DrawingMode.TREATMENT ? "#fff" : "#00d4ad",
          borderColor: "#00d4ad",
          backgroundColor: drawingMode === DrawingMode.TREATMENT ? "#00d4ad" : "transparent",
          "&:hover": {
            backgroundColor: drawingMode === DrawingMode.TREATMENT ? "#00b899" : "rgba(0, 212, 173, 0.1)",
          },
        }}
      >
        Upload File
      </Button>
    </Box>
  );

  return (
    <>
      <FilterModal
        open={filterModalOpen}
        onClose={() => setFilterModalOpen(false)}
        title="Treatments"
        attributes={attributes}
        filters={filters}
        onFiltersChange={handleFiltersChange}
      />
      
      <LayerControlItem
        title="Treatments"
        icon="agriculture"
        visible={layer.active ?? false}
        onToggleVisibility={handleToggleVisibility}
        enabled={!!layer.rawSource}
        filterCount={filters.length}
        onFilterClick={() => setFilterModalOpen(true)}
        filterDisabled={attributes.length === 0}
        filterDisabledTooltip="No data loaded — add treatments to currently selected strategy to enable filters"
        drawingContent={drawingContent}
        expanded={expanded}
        onExpandedChange={onExpandedChange}
      />
    </>
  );
};

export default TreatmentsControl;
